import Vue from 'vue';
import axios from "axios";
import {
    format as date_format,
    parse as date_parse,
    eachDay as date_eachDay,
    subDays as date_subDays,
    subWeeks as date_subWeeks,
    subMonths as date_subMonths
} from "date-fns";
import { formatCapitalizeFirst, filterDate } from "./filter";
import _cloneDeep from "lodash/cloneDeep";

const tzoffset = new Date().getTimezoneOffset() * 60 * 1000;

const checkPropsSchema = {
        label: {
            title: "Label",
            field: "label",
            value: function(check, field){
                if(check.label) return check.label;
                if(check.target) return check.target;
                return check._id;
            },
            // link: function(val){ return (val.substr(0,4) === "http"); },
            show: false
        },
        target: {
            title: "Target",
            field: "target",
            link: function(check, field){ return (check[field] && check[field].length && check[field].substr(0,4) === "http"); },
            show: false
        },
        state: {
            title: "Current Status",
            field: "state",
            value: function(check, field){
                if(check.enable === "inactive") return "Inactive";
                if(check.state === undefined) return "Not Running";
                if(check.state) return "Up";
                return "Down";
            },
            style: function(check, field){
                if(check.enable === "inactive") return "inactive";
                if(check.state === undefined) return "inactive";
                if(check.state) return "isup";
                return "isdown";
            },
            show: true
        },
        type: {
            title: "Check Type",
            field: "type",
            show: true
        },
        interval: {
            title: "Interval",
            field: "interval",
            show: true
        },
        status: {
            title: "Assignment State",
            field: "status",
            value: function(check, field){ return formatCapitalizeFirst(check[field]); },
            show: false
        },
        threshold: {
            title: "Timeout / Threshold",
            field: "threshold",
            value: function(check, field){ return (check[field]) ? check[field] + " seconds" : "" ; },
            show: true
        },
        created: {
            title: "Created Date",
            field: "created",
            value: function(check, field){ return filterDate(check[field]); },
            show: false
        },
        description: {
            title: "Description",
            field: "description",
            show: false
        }
};

// @TODO move this to config?
const resultsSchema = {
    jobid: { text: "ID", align: "start", sortable: true, value: "jobid", show: false },
    time: { text: "Time", align: "start", sortable: true, value: "time", show: true },
    l:    { text: "Location", align: "start", sortable: true, value: "l", show: true },
    rt:   { text: "Run Time (ms)", align: "end", sortable: false, value: "rt", show: true },
    sc:   { text: "Response", align: "start", sortable: false, value: "sc", show: true },
    m:    { text: "Message", align: "start", sortable: true, value: "m" , show: true},
    su:   { text: " ", label: "Status", align: "start", sortable: true, value: "su", show: true }
};

const boolfields = {
    "hidecheckproperties": "showcheckproperties",
    // "hidechecknote1": "showchecknote1",
    // "hidechecknote2": "showchecknote2",
    "hidenote1": "shownote1",
    "hidenote2": "shownote2",
    "hidechart": "showchart",
    "hideevents": "showevents",
    "hideuptime": "showuptime",
    "hideresultslist": "showresultslist",
    // "showchart": "hidechart",
    // "shownote1": "hidenote1",
    // "shownote1": "hidenote1",
    "showresultstoggle": "hideresultstoggle"
};

export default {
    namespaced: true,
    state: {
        baseurl: '/reports/checkstatus/',
        jobid: false,
        reportid: false,
        reportConfig: false,   // the config object for check status reports generally (not just this check)
        reportCheck: false,    // properties for the check itself
        reportResults: false,  // data for results records for this check, used for the chart and table displays
        reportEvents: false,   // data for events for this check
        reportUptime: false,   // data for uptime for this check
        eventParams: false,
        checkPropsSchema: checkPropsSchema,
        resultsSchema: resultsSchema,
        accessphrase: false,
        error: false,
        tzoffset: tzoffset
    },
    mutations: {
        // sets local state version of the check
        setReportCheck (state, val) {
            // console.log("setReportCheck", val)
            state.reportCheck = {...val};
            state.jobid = val._id;
            if(state.reportConfig){
                Vue.set(state.reportConfig, 'reportstatus', ((state.reportConfig.enable !== false && val.public) ? 'public' : 'private'))
            }

        },
        // sets local state version of the check
        setReportCheckField (state, val) {
            // console.log('setReportCheckField', val)
            Vue.set(state.reportCheck, val.field, val.value);
            if(val.field === "public" && state.reportConfig){
                // console.log('setting reportstatus as well', ((state.reportConfig.enable !== false && val.value) ? 'public' : 'private'));
                Vue.set(state.reportConfig, 'reportstatus', ((state.reportConfig.enable !== false && val.value) ? 'public' : 'private'))
            }
        },
        setReportConfig (state, val) {
            if(val && val.hasOwnProperty('enable') && state.reportCheck){
                val.reportstatus = (val.enable !== false && state.reportCheck.public) ? 'public' : 'private';
            }
            for(let k in val){
                if(boolfields[k]){ val[boolfields[k]] = !val[k];}
            }
            state.reportConfig = {...val};
        },
        setReportConfigField (state, val) {
            Vue.set(state.reportConfig, val.field, val.value);
        },
        setReportCheckConfig (state, val) {
            // console.log('mutating check report config', state.reportConfig, val);
            if(!val || !val.checkid) return false;
            if(!state.reportConfig){
                state.reportConfig = { check: {} };
            } else if(!state.reportConfig.check){
                Vue.set(state.reportConfig, 'check', {} );
            }
            for(let k in val){
                if(k === 'checkid') continue;
                // console.log('processing', k);
                Vue.set(state.reportConfig.check, k, val[k]);
                if(k === "resultscharts"){
                    state.reportConfig.check.resultscharts = {...val[k]}
                    // for(let j in val[k]){
                    //     console.log('setting', k, j);
                    //     Vue.set(state.reportConfig.check.resultscharts, j, val[k][j]);
                    // }
                }
            }
            // console.log('report check after changes', state.reportConfig.check);
        },
        setReportId (state, val) {
            state.reportid = val;
        },
        setReportResults (state, val) {
            state.reportResults = [...val];
        },
        setError (state, val) {
            state.error = Object.assign({}, val);
        },
        setEventDays (state, val) {
            // console.log('setEventDay', val);
            for(let k in val){
                Vue.set(state.reportEvents, k, val[k]);
            }
        },
        setEventsList (state, val) {
            // console.log("setResults mutation", val);
            state.reportEvents = Object.assign({}, state.reportEvents, val);
        },
        setEventParams (state, val) {
            state.eventParams = {...val};
        },
        setReportUptime (state, val) {
            state.reportUptime = {...val};
        },
        removeNote (state, val) {
            // console.log("Events mutation removeNote", state.reportEvents[val.day], val);
            if(
                state.reportEvents[val.day] &&
                state.reportEvents[val.day][val.idx] &&
                state.reportEvents[val.day][val.idx].data &&
                state.reportEvents[val.day][val.idx].data[val.dataidx]
            ){
                state.reportEvents[val.day][val.idx].data.splice(val.dataidx, 1);
            }
        },
        setNote (state, val) {
            // reportEvents[day][k].data[i].record
            // console.log("Events mutation setNote",  state.reportEvents[val.day][val.idx].data, val.dataidx, val.record);
            val.idx = val.idx - 0;

            if(state.reportEvents[val.day][val.idx] === undefined) state.reportEvents[val.day][val.idx] = {};
            if(state.reportEvents[val.day][val.idx].data === undefined){
                state.reportEvents[val.day][val.idx].data = [];
            }

            val.dataidx = (val.dataidx === "new" || (val.dataidx != val.dataidx - 0)) ? state.reportEvents[val.day][val.idx].data.length : val.dataidx - 0;


            Vue.set(state.reportEvents[val.day][val.idx].data, val.dataidx, val.record);
        },
        setAccessPhrase (state, val) {
            state.accessphrase = val;
        }

    },
    actions: {
        getReport: function({ commit, state }, options){
            options = options || {};
            let reportid = options.reportid || (state.reportConfig && state.reportConfig.reportid);
            // console.log('getReport', options);
            if (!reportid){
                // console.log("No reportid", options);
                return Promise.resolve();
            }

            commit("setReportId", reportid);

            if(options.accessphrase){
                commit('setAccessPhrase', options.accessphrase);
            }

            let params = (options && options.params) ? {...options.params} : {};
            if(state.accessphrase && state.accessphrase.length) params.accessphrase = state.accessphrase;

            return axios.post(state.baseurl + reportid + "/check", params)
                .then( (result) => {
                    if (result.data){
                        if (result.data.reportconfig && !options.skipconfig){
                            let hascheckconfig = !!result.data.reportconfig.check;
                            result.data.reportconfig.check = result.data.reportconfig.check || {};

                            for(let k in boolfields){
                                // some UI elements treat undefined differently than false
                                result.data.reportconfig[k] = !!result.data.reportconfig[k];
                                result.data.reportconfig[boolfields[k]] = !result.data.reportconfig[k];
                                if(hascheckconfig){
                                    result.data.reportconfig.check[k] = !!result.data.reportconfig.check[k];
                                    result.data.reportconfig.check[boolfields[k]] = !result.data.reportconfig.check[k];
                                }
                            }

                            commit('setReportConfig', result.data.reportconfig);
                        }
                        if (result.data.check && !options.skipcheck){
                            commit('setReportCheck', result.data.check);
                        }
                        commit('setError', false);
                        return result.data.check;
                    }
                })
                .catch( (error) => {
                    console.log("ERROR", error.response || error);
                    error = (error && error.response && error) || {
                        response: 500,
                        data: {}
                    }
                    commit('setError', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        message: ((error.response.data && error.response.data.error) || "Report unavailable"),
                        type: error.response.data && error.response.data.type,
                        login: error.response.data && error.response.data.login,
                    });
                })

        },
        getResults: function({ commit, state }, options){
            options = options || {};
            let reportid = options.reportid || state.reportid || (state.reportConfig && state.reportConfig.reportid);
            if (!reportid){
                console.log("Can't find a report ID", reportid, state.reportConfig);
                return Promise.resolve();
            }
            let params = {};

            if(state.checkResults){
                // console.log("getResults going back to the well")
                // we have some
            } else {
                // ain't got some
                // console.log('getResults options', options);
                params.limit = options.limit || 300;
            }

            return axios.post(state.baseurl + reportid + "/results", params)
                .then( (result) => {
                    // console.log('results', result);
                    let dlength = (result.data && result.data.length) ? result.data.length : 0;
                    for(let i=0;i<dlength;i++){
                        result.data[i].time = date_format(date_parse(result.data[i].s),"YYYY-MM-DD HH:mm");
                        result.data[i].up = result.data[i].s ? "PASS" : "FAIL";
                    }
                    if (result.data){
                        commit('setReportResults', result.data);
                        commit('setError', false);
                    }
                }).catch( (error) => {
                    console.log("ERROR", error.response || error);
                    if(!state.error){
                        error = (error && error.response && error) || {
                            response: 500,
                            data: {}
                        }
                        commit('setError', {
                            status: error.response.status,
                            statusText: error.response.statusText,
                            message: ((error.response.data && error.response.data.error) || "Report unavailable"),
                            type: error.response.data && error.response.data.type
                        });
                    }
                })



        },
        getEvents: function({commit, state}, options){
            options = options || {};
            let reportid = options.reportid || state.reportid || (state.reportConfig && state.reportConfig.reportid);
            if (!reportid){
                console.log("getEvents needs a reportid", options);
                return Promise.resolve();
            }

            let params = {
                offset: tzoffset
            };
            // if(options.start) params.start = options.start;
            // if(options.end) params.end = options.end;
            // if(options.page && options.page == options.page - 0) params.eventpage = options.page;
            const fields = {
                eventpage: function(v){ return (options.eventpage == options.eventpage - 0) ? options.eventpage : undefined; },
                start: true,
                end: true,
                eventsinterval: true
            };
            for(let f in fields){
                if(options[f]){ params[f] = (typeof fields[f] === "function") ? fields[f](options[f]) : options[f]; }
            }

            // console.log('getEvents calling post with', options, params);

            return axios.post(state.baseurl + reportid + "/checkevents", params)
                .then( (result) => {
                    // console.log('getEvents results', result);
                    if (result && result.data){
                        // if(result.data.events) commit('setEvents', result.data.events);
                        // if(result.data.params) commit('setEventParams', result.data.params);

                        if(result.data.params) commit('setEventParams', result.data.params);
                        if(result.data.events) result.data = result.data.events;
                        let days = {};
                        // options.eventsinterval = options.eventsinterval || (state.reportConfig.check && state.reportConfig.check.eventsinterval) || state.reportConfig.eventsinterval || "months";
                        // options.showemptyeventdays = options.showemptyeventdays || (state.reportConfig.check && state.reportConfig.check.showemptyeventdays) || state.reportConfig.showemptyeventdays || false;
                        // console.log("event options", options)
                        // if(options.showemptyeventdays){
                        //     let now = new Date();
                        //     params.eventpage =  params.eventpage || 0
                        //     if(!options.startdate){
                        //         if(options.eventsinterval === "days"){
                        //             options.startdate = date_subDays(now, params.eventpage + 1);
                        //         }else if (options.eventsinterval === "weeks"){
                        //             options.startdate =  date_subWeeks(now, params.eventpage + 1);
                        //         } else {
                        //             options.startdate =  date_subMonths(now, params.eventpage + 1);
                        //         }
                        //     }
                        //     if (!options.enddate) {
                        //         if (!params.eventpage ) {
                        //             options.enddate = now;
                        //         } else if(options.eventsinterval === "days"){
                        //             options.enddate = date_subDays(now, params.eventpage );
                        //         }else if (options.eventsinterval === "weeks"){
                        //             options.enddate =  date_subWeeks(now, params.eventpage );
                        //         } else {
                        //             options.enddate =  date_subMonths(now, params.eventpage );
                        //         }
                        //     }
                        //     let datelist = date_eachDay(options.startdate, options.enddate);
                        //     // console.log("Showing empty event days.", options)
                        //     let d = datelist.length;
                        //     while( d-- ){
                        //         days[date_format(datelist[d], 'YYYY-MM-DD')] = [];
                        //     }
                        // }

                        let datalength = result.data.length;
                        for( let i=0; i < datalength; i++ ){
                            result.data[i].startlocal = result.data[i].start - tzoffset;
                            if (result.data[i].end) result.data[i].endlocal = result.data[i].end - tzoffset;
                            let thedate = date_format(date_parse(result.data[i].start), 'YYYY-MM-DD');
                            result.data[i].key = result.data[i].jobid + "-" + result.data[i].start;
                            if(result.data[i].data){
                                let dlen = result.data[i].data.length
                                for(let j=0; j < dlen; j++){
                                    result.data[i].data[j].key =  result.data[i].key + '-' + result.data[i].data[j].created;
                                    // result.data[i].data[j] = {
                                    //     id: result.data[i].data[j].jobid,
                                    //     httpstatus: result.data[i].data[j].httpstatus,
                                    //     data: result.data[i].data[j].data
                                    // }
                                }

                            }
                            days[thedate] = days[thedate] || [];
                            days[thedate].push(result.data[i]);
                        }

                        if(!state.reportEvents || Object.keys(days).length > 14){
                            // console.log('calling commit for setEventsList', days);
                            commit('setEventsList', days);
                        } else {
                            // console.log('calling commit for setEventDays');
                            commit('setEventDays', days);
                        }

                        commit('setError', false);
                    }
                }).catch( (error) => {
                    console.log("ERROR", error.response || error);
                    if(!state.error){
                        error = (error && error.response && error) || {
                            response: 500,
                            data: {}
                        }
                        commit('setError', {
                            status: error.response.status,
                            statusText: error.response.statusText,
                            message: ((error.response.data && error.response.data.error) || "Report unavailable"),
                            type: error.response.data && error.response.data.type
                        });
                    }
                })
        },

        setReportCheck: function({commit, state}, options){
            if(!options || !options.field || options.value === undefined) return Promise.reject(new Error('invalid options'));

            commit('setReportCheckField', options);


            return Promise.resolve();
        },

        setReportConfig: function({commit, state}, options){
            // console.log('store_reports_checkstatus.setReportConfig', options);
            if(!options) return Promise.reject(new Error('invalid options'));
            for(let k in options){
                if(boolfields[k]){
                    options[ boolfields[k]] = !options[k];
                }
            }

            for(let key in options){
                commit('setReportConfigField', {field: key, value:options[key]});
            }

            // special handlers

            let reportConfig = Object.assign({}, state.reportConfig, options);

            if(reportConfig.checks) delete reportConfig.checks;
            if(reportConfig.options) delete reportConfig.options;
            if(!reportConfig.reportname) reportConfig.reportname = 'checkstatus';
            // console.log('setting report config', reportConfig);

            return axios.post('/account/reporting/checkstatus', reportConfig)
                .then( (result) => {
                    // console.log('result from sending reportConfig update', result);
                    if(result && result.data && result.data.updated){
                        commit('setReportConfigField', {field: 'key', value: result.data.updated });
                    }
                })
                .catch( function (error){
                    console.log("ERROR", error.response || error);
                    error = (error && error.response && error) || {
                        response: 500,
                        data: {}
                    }
                    commit('setError', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        message: ((error.response.data && error.response.data.error) || "Report unavailable"),
                        type: error.response.data && error.response.data.type
                    });
                })

        },

        setReportCheckConfig: function({commit, state}, options){
            // console.log('setReportCheckConfig start', options);
            if (!options){
                return Promise.reject(new Error('Missing parameters'));
            }

            // let reportConfig = Object.assign({}, state.reportConfig);
            let reportConfig = _cloneDeep(state.reportConfig);
            reportConfig.setCheck = {
                checkid: state.jobid
            };

            // for this report the key and report type are both checkstatus.
            reportConfig.reportname = reportConfig.reportname || "checkstatus";
            reportConfig.report = reportConfig.report || "checkstatus";

            for(let k in options){
                if(boolfields[k]){
                    options[ boolfields[k]] = !options[k];
                }
            }

            // modifies reportConfig.setCheck with what's in options
            Object.assign(reportConfig.setCheck, reportConfig.check || {}, options);

            // here we need to push the change into state
            commit('setReportCheckConfig', reportConfig.setCheck);

            if(reportConfig.check){
                delete reportConfig.check;
            }

            // if(reportConfig.report)

            // console.log('setReportCheck record', reportConfig);
            return axios.post('/account/reporting/checkstatus', reportConfig)
                .then( (result) => {
                    // console.log('result from sending reportConfig update', result);
                    return result;
                })
                .catch( function (error){
                    console.log("ERROR", error.response || error);
                    error = (error && error.response && error) || {
                        response: 500,
                        data: {}
                    }
                    commit('setError', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        message: ((error.response.data && error.response.data.error) || "Report unavailable"),
                        type: error.response.data && error.response.data.type
                    });
                })
        },

        setEventDataNote: function({commit, state}, options){
            // console.log('setEventDataNote', options);
            // @TODO this should possibly go in a separate module, but I'm not sure if we'll ever have more than this one method.

            if(!options || !options.data || !options.start){
                return Promise.reject(new Error('Missing parameters.  data and start are required.'));
            }

            if(options.start && (options.start - 0 == options.start)) options.start = options.start - 0;

            let record = {
                jobid: state.reportCheck._id,
                start: options.start,
                created: options.created || Date.now(),
                type: options.type || "note",
                visibility: options.visibility || "private",
                data: options.data
            };
            if(options.created) record.update = true;

            let day = date_format(date_parse(options.start), 'YYYY-MM-DD');

            let idx, dataidx;
            for(let k in state.reportEvents[day]){
                if(state.reportEvents[day][k].start === record.start){
                    idx = k;
                    if(state.reportEvents[day][k].data){
                        for(let i in state.reportEvents[day][k].data){
                            if(state.reportEvents[day][k].data[i].type === "note"){
                                dataidx = i;
                            }
                        }
                    }
                }
            }
            if(!idx && idx !== 0){
                return Promise.reject(new Error("I cannot find that event: "));
            } else {
                // console.log('saving note', idx, record);
            }

            record.created = record.created - 0;

            commit('setNote', {
                day: day,
                idx: idx,
                dataidx: dataidx || "new",
                record: record
            });

            // return Promise.resolve(record);

            return axios.post('/events/saveeventdata', record)
                .then( (result) => {
                    // console.log(result);
                    return result;
                })
                .catch( function (error){
                    console.log("ERROR", error.response || error);
                    error = (error && error.response && error) || {
                        response: 500,
                        data: {}
                    }
                    return error;
                });
        },

        removeEventDataNote: function({commit, state}, options) {
            if(!options || !options.jobid || !options.start || !options.created){
                return Promise.reject(new Error("Missing prameters.  jobid, start and created are required."));
            }

            let record = {
                jobid: options.jobid,
                start: options.start - 0,
                created: options.created - 0
            };

            let day = date_format(date_parse(record.start), 'YYYY-MM-DD');
            let idx, dataidx;
            for(let k in state.reportEvents[day]){
                if(state.reportEvents[day][k].start === record.start){
                    idx = k;
                    if(state.reportEvents[day][k].data){
                        for(let i in state.reportEvents[day][k].data){
                            if(state.reportEvents[day][k].data[i].type === "note"){
                                dataidx = i;
                            }
                        }
                    }
                }
            }
            if(!idx){
                // console.log(day, idx, dataidx, record, options)
                return Promise.reject(new Error("I cannot find that note."));
            }

            commit('removeNote', {
                day: day,
                idx: idx,
                dataidx: dataidx
            });

            return axios.post('/events/removeeventdata', record)
                .then( (result) => {
                    // console.log(result);
                    return result;
                })
                .catch( function (error){
                    console.log("ERROR", error.response || error);
                    error = (error && error.response && error) || {
                        response: 500,
                        data: {}
                    }
                    return error;
                });
        },
        getUptime: function({commit, state}, options) {
            options = options || {};
            let reportid = options.reportid || (state.reportConfig && state.reportConfig.reportid);
            if (!reportid){
                console.log("getUptime needs a reportid", options);
                return Promise.resolve();
            }

            let params = {
                offset: tzoffset
            };
            return axios.post(state.baseurl + reportid + "/checkuptime", params)
            .then( (result) => {
                if (result.data){
                    commit('setReportUptime', result.data);
                    commit('setError', false);
                }
            }).catch( (error) => {
                console.log("ERROR", error.response || error);
                if(!state.error){
                    error = (error && error.response && error) || {
                        response: 500,
                        data: {}
                    }
                    commit('setError', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        message: ((error.response.data && error.response.data.error) || "Report unavailable"),
                        type: error.response.data && error.response.data.type
                    });
                }
            })
        }
    },
    getters: {
        mergedConfig: state => {
            let config = Object.assign({}, state.reportConfig.check);
            for(let k in config){
                if(typeof config[k] === "string" && !config[k].length) delete config[k];
            }
            config = Object.assign({}, state.reportConfig, config);
            if(config.check) delete config.check;
            // console.log("mergedConfig out", config);
            return config;
        }
    }

}